import type { BillingPlan, BillingStatus } from '@mio/shared';

const DAY_MS = 24 * 60 * 60 * 1000;
const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const date = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export function formatAmount(plan: Pick<BillingPlan, 'amount'>) {
  return money.format(plan.amount / 100);
}

export function formatDate(iso: string) {
  return date.format(new Date(iso));
}

export function daysLeft(iso: string, now = new Date()) {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now.getTime()) / DAY_MS));
}

export interface PlanSummary {
  title: string;
  detail: string;
  /** Whether the learner should be nudged towards the plan page. */
  upgrade: boolean;
}

/** One line for the sidebar and the account page, derived the same way everywhere. */
export function summarizePlan(status: BillingStatus, now = new Date()): PlanSummary {
  const { access, subscription, pass, trial } = status;
  switch (access.state) {
    case 'unlimited':
      return { title: 'Full access', detail: 'No expiry on this account.', upgrade: false };
    case 'suspended':
      return {
        title: 'Account suspended',
        detail: 'Contact support to restore access.',
        upgrade: false,
      };
    case 'expired':
      return {
        title: trial && !subscription && !pass ? 'Trial ended' : 'Access ended',
        detail: access.expiresAt
          ? `Ended ${formatDate(access.expiresAt)}.`
          : 'Choose a plan to continue.',
        upgrade: true,
      };
    case 'grace':
      return {
        title: 'Payment failed',
        detail: access.expiresAt
          ? `Update your card by ${formatDate(access.expiresAt)} to keep access.`
          : 'Update your card to keep access.',
        upgrade: true,
      };
    case 'trial': {
      const days = access.expiresAt ? daysLeft(access.expiresAt, now) : 0;
      return {
        title: `Free trial · ${days} ${days === 1 ? 'day' : 'days'} left`,
        detail: access.expiresAt ? `Ends ${formatDate(access.expiresAt)}.` : '',
        upgrade: true,
      };
    }
    default: {
      if (pass)
        return {
          title: '6-month pass',
          detail: `Access until ${formatDate(pass.accessUntil)}.`,
          upgrade: false,
        };
      if (subscription)
        return {
          title: 'Monthly plan',
          detail: subscription.cancelAt
            ? `Cancels ${formatDate(subscription.cancelAt)}.`
            : subscription.currentPeriodEnd
              ? `Renews ${formatDate(subscription.currentPeriodEnd)}.`
              : 'Renews monthly.',
          upgrade: false,
        };
      return {
        title: 'Active',
        detail: access.expiresAt ? `Access until ${formatDate(access.expiresAt)}.` : '',
        upgrade: false,
      };
    }
  }
}
